import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Menu, 
  X, 
  Home, 
  Building2, 
  LogIn, 
  UserPlus, 
  Plus, 
  LayoutDashboard, 
  User, 
  MessageCircle, 
  FileText,
  CreditCard,
  LogOut,
  Bell
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useMessageStore } from '../stores/messageStore';
import { useLeaseStore } from '../stores/leaseStore';
import { useListingStore } from '../stores/listingStore';

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const { isAuthenticated, logout, user } = useAuth();
  const { getUnreadCount } = useMessageStore();
  const { getRequestsByUser } = useLeaseStore();
  const { listings } = useListingStore();

  const unreadMessages = user ? getUnreadCount(user.id) : 0;
  
  // Only get pending requests if user is an owner and has listings
  const userListings = user ? listings.filter(listing => listing.ownerId === user.id) : [];
  const hasListings = userListings.length > 0;
  const pendingRequests = user && user.role === 'owner' && hasListings 
    ? getRequestsByUser(user.id, 'owner').filter(req => req.status === 'pending').length 
    : 0;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="bg-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2">
              <Building2 className="h-8 w-8 text-indigo-600" />
              <span className="text-xl font-bold text-gray-900">SubleaseHero</span>
            </Link>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-4">
            <Link to="/" className="flex items-center space-x-1 text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium">
              <Home className="h-4 w-4" />
              <span>Home</span>
            </Link>
            <Link to="/listings" className="text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium">
              Find Listings
            </Link>
            <Link 
              to="/list-property" 
              className="flex items-center space-x-1 bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>List Property</span>
            </Link>
            
            {isAuthenticated ? (
              <>
                <Link to="/dashboard" className="flex items-center space-x-1 text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium">
                  <LayoutDashboard className="h-4 w-4" />
                  <span>Dashboard</span>
                </Link>
                
                {/* Profile Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowProfileMenu(!showProfileMenu)}
                    onMouseEnter={() => setShowProfileMenu(true)}
                    className="flex items-center space-x-1 text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium"
                  >
                    <User className="h-4 w-4" />
                    <span>Account</span>
                    {(unreadMessages > 0 || pendingRequests > 0) && (
                      <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white text-xs flex items-center justify-center rounded-full">
                        {unreadMessages + pendingRequests}
                      </span>
                    )}
                  </button>

                  {showProfileMenu && (
                    <div 
                      className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50"
                      onMouseLeave={() => setShowProfileMenu(false)}
                    >
                      <Link
                        to="/profile"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <User className="h-4 w-4 mr-2" />
                        Profile Settings
                      </Link>
                      <Link
                        to="/messages"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <MessageCircle className="h-4 w-4 mr-2" />
                        Messages
                        {unreadMessages > 0 && (
                          <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                            {unreadMessages}
                          </span>
                        )}
                      </Link>
                      {user?.role === 'owner' && hasListings && (
                        <Link
                          to="/lease-requests"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          Lease Requests
                          {pendingRequests > 0 && (
                            <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                              {pendingRequests}
                            </span>
                          )}
                        </Link>
                      )}
                      <Link
                        to="/billing"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <CreditCard className="h-4 w-4 mr-2" />
                        Billing & Payments
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link to="/login" className="flex items-center space-x-1 text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium">
                  <LogIn className="h-4 w-4" />
                  <span>Login</span>
                </Link>
                <Link to="/register" className="flex items-center space-x-1 bg-gray-900 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-800">
                  <UserPlus className="h-4 w-4" />
                  <span>Sign Up</span>
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-indigo-600 focus:outline-none"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <Link to="/" className="flex items-center space-x-2 text-gray-700 hover:text-indigo-600 block px-3 py-2 rounded-md text-base font-medium">
              <Home className="h-5 w-5" />
              <span>Home</span>
            </Link>
            <Link to="/listings" className="text-gray-700 hover:text-indigo-600 block px-3 py-2 rounded-md text-base font-medium">
              Find Listings
            </Link>
            <Link 
              to="/list-property" 
              className="flex items-center space-x-2 bg-indigo-600 text-white block px-3 py-2 rounded-md text-base font-medium"
            >
              <Plus className="h-5 w-5" />
              <span>List Property</span>
            </Link>
            
            {isAuthenticated ? (
              <>
                <Link to="/dashboard" className="flex items-center space-x-2 text-gray-700 hover:text-indigo-600 block px-3 py-2 rounded-md text-base font-medium">
                  <LayoutDashboard className="h-5 w-5" />
                  <span>Dashboard</span>
                </Link>
                <Link to="/profile" className="flex items-center space-x-2 text-gray-700 hover:text-indigo-600 block px-3 py-2 rounded-md text-base font-medium">
                  <User className="h-5 w-5" />
                  <span>Profile Settings</span>
                </Link>
                <Link to="/messages" className="flex items-center space-x-2 text-gray-700 hover:text-indigo-600 block px-3 py-2 rounded-md text-base font-medium">
                  <MessageCircle className="h-5 w-5" />
                  <span>Messages</span>
                  {unreadMessages > 0 && (
                    <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                      {unreadMessages}
                    </span>
                  )}
                </Link>
                {user?.role === 'owner' && hasListings && (
                  <Link to="/lease-requests" className="flex items-center space-x-2 text-gray-700 hover:text-indigo-600 block px-3 py-2 rounded-md text-base font-medium">
                    <FileText className="h-5 w-5" />
                    <span>Lease Requests</span>
                    {pendingRequests > 0 && (
                      <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                        {pendingRequests}
                      </span>
                    )}
                  </Link>
                )}
                <Link to="/billing" className="flex items-center space-x-2 text-gray-700 hover:text-indigo-600 block px-3 py-2 rounded-md text-base font-medium">
                  <CreditCard className="h-5 w-5" />
                  <span>Billing & Payments</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-2 text-gray-700 hover:text-indigo-600 block px-3 py-2 rounded-md text-base font-medium w-full text-left"
                >
                  <LogOut className="h-5 w-5" />
                  <span>Logout</span>
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="flex items-center space-x-2 text-gray-700 hover:text-indigo-600 block px-3 py-2 rounded-md text-base font-medium">
                  <LogIn className="h-5 w-5" />
                  <span>Login</span>
                </Link>
                <Link to="/register" className="flex items-center space-x-2 bg-gray-900 text-white block px-3 py-2 rounded-md text-base font-medium">
                  <UserPlus className="h-5 w-5" />
                  <span>Sign Up</span>
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;